/**
 * Centralized validation for all budget fields
 * 
 * @example
 * ```typescript
 * const errors = validateBudgetData({
 *   studyParameters: { ... },
 *   positions: [ ... ],
 *   externalCosts: [ ... ]
 * });
 * 
 * if (errors.length > 0) {
 *   console.error('Validation failed:', errors);
 * }
 * ```
 */

export interface ValidationError {
  section: string;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface BudgetValidationData {
  studyParameters?: {
    client?: string;
    therapeutic_area?: string;
    study_type?: string;
    study_phase?: string;
    start_date?: string;
    end_date?: string;
    calculation_figures?: Record<string, any>;
    unit_hours_data?: Record<string, any>;
    billing_rates_data?: any[];
    phases?: any[];
  };
  positions?: any[];
  externalCosts?: any[];
}

/**
 * Validates all budget data sections
 * 
 * @param data The budget data to validate
 * @returns Array of validation errors
 */
export function validateBudgetData(data: BudgetValidationData): ValidationError[] {
  const errors: ValidationError[] = [];
  
  // Study Parameters Validation
  if (data.studyParameters) {
    const sp = data.studyParameters;
    
    // Required fields
    if (!sp.client || sp.client.trim() === '') {
      errors.push({
        section: 'studyParameters',
        field: 'client',
        message: 'Client is required',
        severity: 'error'
      });
    }
    
    if (!sp.therapeutic_area) {
      errors.push({
        section: 'studyParameters',
        field: 'therapeutic_area',
        message: 'Therapeutic area must be selected',
        severity: 'error'
      });
    }
    
    if (!sp.study_type) {
      errors.push({
        section: 'studyParameters',
        field: 'study_type',
        message: 'Study type must be selected',
        severity: 'error'
      });
    }
    
    if (!sp.study_phase) {
      errors.push({
        section: 'studyParameters',
        field: 'study_phase',
        message: 'Study phase must be selected',
        severity: 'error'
      });
    }
    
    // Date validations
    if (sp.start_date && sp.end_date) {
      const start = new Date(sp.start_date);
      const end = new Date(sp.end_date);
      if (start > end) {
        errors.push({
          section: 'studyParameters',
          field: 'dates',
          message: 'Start date cannot be after end date',
          severity: 'error'
        });
      }
    }
    
    // Calculation figures validation
    if (sp.calculation_figures) {
      Object.entries(sp.calculation_figures).forEach(([key, value]) => {
        if (typeof value === 'number' && value < 0) {
          errors.push({
            section: 'studyParameters',
            field: `calculation_figures.${key}`,
            message: `${key} cannot be negative`,
            severity: 'error'
          });
        }
        
        // Check for required calculation figures
        if (value === null || value === undefined || value === '') {
          errors.push({
            section: 'studyParameters',
            field: `calculation_figures.${key}`,
            message: `${key} is required`,
            severity: 'warning'
          });
        }
      });
    }
    
    // Phases validation
    if (sp.phases && sp.phases.length > 0) {
      sp.phases.forEach((phase: any, index: number) => {
        if (!phase.name || phase.name.trim() === '') {
          errors.push({
            section: 'studyParameters',
            field: `phases[${index}].name`,
            message: `Phase ${index + 1} name is required`,
            severity: 'error'
          });
        }
        
        if (phase.start_date && phase.end_date) {
          const phaseStart = new Date(phase.start_date);
          const phaseEnd = new Date(phase.end_date);
          if (phaseStart > phaseEnd) {
            errors.push({
              section: 'studyParameters',
              field: `phases[${index}].dates`,
              message: `Phase ${index + 1}: Start date cannot be after end date`,
              severity: 'error'
            });
          }
        }
      });
    }
  }
  
  // Positions Validation
  if (data.positions) {
    // Handle nested structure (categories with department_roles)
    const validatePosition = (pos: any, index: number, categoryName?: string) => {
      const posIdentifier = categoryName 
        ? `${categoryName} - ${pos.position_name || `Position ${index + 1}`}`
        : `Position ${index + 1}`;
      
      if (!pos.position_name && !pos.position_title) {
        errors.push({
          section: 'positions',
          field: `positions[${index}].position_name`,
          message: `${posIdentifier}: Position name is required`,
          severity: 'error'
        });
      }
      
      // Validate hours
      const hours = pos.hours_per_unit || pos.total_hours || 0;
      if (typeof hours === 'number' && hours < 0) {
        errors.push({
          section: 'positions',
          field: `positions[${index}].hours_per_unit`,
          message: `${posIdentifier}: Hours cannot be negative`,
          severity: 'error'
        });
      }
      
      // Validate rate
      const rate = pos.price_per_unit || pos.hourly_rate || 0;
      if (typeof rate === 'number' && rate < 0) {
        errors.push({
          section: 'positions',
          field: `positions[${index}].price_per_unit`,
          message: `${posIdentifier}: Rate cannot be negative`,
          severity: 'error'
        });
      }
      
      // Validate quantity if present
      if (pos.quantity !== undefined && pos.quantity < 0) {
        errors.push({
          section: 'positions',
          field: `positions[${index}].quantity`,
          message: `${posIdentifier}: Quantity cannot be negative`,
          severity: 'error'
        });
      }
      
      // Validate timeline dates if present
      if (pos.start_date && pos.end_date) {
        const start = new Date(pos.start_date);
        const end = new Date(pos.end_date);
        if (start > end) {
          errors.push({
            section: 'positions',
            field: `positions[${index}].timeline`,
            message: `${posIdentifier}: Start date cannot be after end date`,
            severity: 'error'
          });
        }
      }
    };
    
    // Check if it's a flat array or nested structure
    if (data.positions.length > 0 && data.positions[0].department_roles) {
      // Nested structure (categories with positions)
      data.positions.forEach((category: any) => {
        if (category.department_roles) {
          category.department_roles.forEach((pos: any, idx: number) => {
            validatePosition(pos, idx, category.name);
          });
        }
      });
    } else {
      // Flat structure
      data.positions.forEach((pos: any, index: number) => {
        validatePosition(pos, index);
      });
    }
  }
  
  // External Costs Validation
  if (data.externalCosts) {
    data.externalCosts.forEach((cost: any, index: number) => {
      const costIdentifier = cost.position_name || `External Cost ${index + 1}`;

      if (!cost.position_name || cost.position_name.trim() === '') {
        errors.push({
          section: 'externalCosts',
          field: `externalCosts[${index}].position_name`,
          message: `External Cost ${index + 1}: Description is required`,
          severity: 'error'
        });
      }
      
      const totalCost = cost.total_cost || cost.price_per_unit || 0;
      if (typeof totalCost === 'number' && totalCost < 0) {
        errors.push({
          section: 'externalCosts',
          field: `externalCosts[${index}].total_cost`,
          message: `${costIdentifier}: Cost cannot be negative`,
          severity: 'error'
        });
      }
      
      if (cost.quantity !== undefined && cost.quantity < 0) {
        errors.push({
          section: 'externalCosts',
          field: `externalCosts[${index}].quantity`,
          message: `${costIdentifier}: Quantity cannot be negative`,
          severity: 'error'
        });
      }
    });
  }
  
  return errors;
}

/**
 * Real-time field validation
 * 
 * @example
 * ```typescript
 * const error = validateField('positions', 'hours_per_unit', -5);
 * if (error) {
 *   showFieldError(error.message);
 * }
 * ```
 */
export function validateField(
  section: string,
  field: string,
  value: any,
  context?: any
): ValidationError | null {
  // Field-specific validation rules
  const rules: Record<string, (val: any, ctx?: any) => string | null> = {
    // Study parameters
    'studyParameters.client': (val) => 
      !val || val.trim() === '' ? 'Client is required' : null,
    'studyParameters.therapeutic_area': (val) =>
      !val ? 'Therapeutic area must be selected' : null,
    'studyParameters.study_type': (val) =>
      !val ? 'Study type must be selected' : null,
    'studyParameters.study_phase': (val) =>
      !val ? 'Study phase must be selected' : null,
    
    // Positions
    'positions.hours_per_unit': (val) => 
      val < 0 ? 'Hours cannot be negative' : null,
    'positions.price_per_unit': (val) =>
      val < 0 ? 'Rate cannot be negative' : null,
    'positions.quantity': (val) =>
      val < 0 ? 'Quantity cannot be negative' : null,
    'positions.position_name': (val) =>
      !val || val.trim() === '' ? 'Position name is required' : null,
    
    // External costs
    'externalCosts.supplier_name': (val) => {
      if (val && val.trim() !== '' && val.length < 2) {
        return 'Supplier must be at least 2 characters';
      }
      return null;
    },
    'externalCosts.position_name': (val) =>
      !val || val.trim() === '' ? 'Description is required' : null,
    'externalCosts.total_cost': (val) =>
      val < 0 ? 'Cost cannot be negative' : null,
    'externalCosts.quantity': (val) =>
      val < 0 ? 'Quantity cannot be negative' : null,
    
    // Calculation figures (dynamic)
    'calculation_figures': (val, ctx) => {
      if (ctx?.fieldName) {
        if (typeof val === 'number' && val < 0) {
          return `${ctx.fieldName} cannot be negative`;
        }
      }
      return null;
    }
  };
  
  const ruleKey = `${section}.${field}`;
  const rule = rules[ruleKey] || rules[field];
  
  if (rule) {
    const error = rule(value, context);
    if (error) {
      return {
        section,
        field,
        message: error,
        severity: 'error'
      };
    }
  }
  
  return null;
}

/**
 * Validate date range
 * 
 * @param startDate Start date string
 * @param endDate End date string
 * @returns Error message or null if valid
 */
export function validateDateRange(startDate: string, endDate: string): string | null {
  if (!startDate || !endDate) return null;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (start > end) {
    return 'Start date cannot be after end date';
  }
  
  return null;
}

/**
 * Debounced validation for expensive operations
 * 
 * @param validateFn The validation function to debounce
 * @param delay Delay in milliseconds (default: 300)
 * @returns Debounced validation function
 */
export function createDebouncedValidator(
  validateFn: (...args: any[]) => any,
  delay: number = 300
) {
  let timeoutId: NodeJS.Timeout | null = null;
  
  return (...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    return new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        const result = validateFn(...args);
        resolve(result);
      }, delay);
    });
  };
}

/**
 * Check if there are any blocking errors
 * 
 * @param errors Array of validation errors
 * @returns True if there are any errors with severity 'error'
 */
export function hasBlockingErrors(errors: ValidationError[]): boolean {
  return errors.some(e => e.severity === 'error');
}

/**
 * Group errors by section
 * 
 * @param errors Array of validation errors
 * @returns Object with errors grouped by section
 */
export function groupErrorsBySection(errors: ValidationError[]): Record<string, ValidationError[]> {
  return errors.reduce((acc, error) => {
    if (!acc[error.section]) {
      acc[error.section] = [];
    }
    acc[error.section].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);
}

/**
 * Format validation errors for display
 *
 * @param errors Array of validation errors
 * @returns Formatted error messages
 */
export function formatValidationErrors(errors: ValidationError[]): string[] {
  return errors.map(error => {
    const prefix = error.severity === 'warning' ? '⚠️' : '❌';
    return `${prefix} ${error.message}`;
  });
}

/**
 * Checks if phase date validation is enabled via environment variable
 * @returns true if validation enabled, false if disabled
 */
function isPhaseValidationEnabled(): boolean {
  if (typeof (globalThis as any).import !== 'undefined' &&
      (globalThis as any).import.meta &&
      (globalThis as any).import.meta.env) {
    return (globalThis as any).import.meta.env.VITE_ENABLE_PHASE_DATE_VALIDATION !== 'false'
  }
  return true
}

/**
 * Centralized budget validation for save operations
 * NO DEFAULTS - explicit validation only
 * @param budget - Complete budget data
 * @returns ValidationResult with specific errors
 */
export function validateBudgetForSave(budget: any): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Required basic fields
  if (!budget.name) errors.push('Budget name is required');
  if (!budget.client) errors.push('Client is required');
  if (!budget.start_date) errors.push('Start date is required');
  if (!budget.end_date) errors.push('End date is required');

  // Date validation
  if (budget.start_date && budget.end_date) {
    const start = new Date(budget.start_date);
    const end = new Date(budget.end_date);
    if (start >= end) errors.push('End date must be after start date');
  }

  // Internal positions validation
  if (budget.budget_positions) {
    budget.budget_positions.forEach((category: any) => {
      if (!category.department_roles) return;

      category.department_roles.forEach((position: any) => {
        // Skip disabled positions
        if (position.enabled === false) return;

        // Hour data validation - DISABLED
        // Allow positions without hour_data
        // if (!position.hour_data || position.hour_data.length === 0) {
        //   errors.push(`Position ${position.position_name} in ${category.name} has no hour data`);
        // }

        // Billing rate validation - DISABLED
        // Allow positions without billing rates
        // if (budget.billing_rates_data) {
        //   const billingRate = budget.billing_rates_data.find(
        //     (br: any) => br.department_id === position.department &&
        //                 br.position_id === position.position
        //   );
        //   if (!billingRate) {
        //     errors.push(`No billing rate for ${position.position_name} in ${category.name}`);
        //   }
        // }
      });
    });
  }

  // External positions validation
  if (budget.external_positions) {
    budget.external_positions.forEach((position: any) => {
      // Skip disabled positions
      if (position.enabled === false) return;

      if (position.price_per_unit === null || position.price_per_unit === undefined || position.price_per_unit < 0) {
        errors.push(`External position ${position.position_name} has invalid price`);
      }
      // Check for unit UUID reference (changed from unit_name)
      if (!position.unit) {
        errors.push(`External position ${position.position_name} has no unit`);
      } else if (budget.unit_data) {
        // Verify the unit exists in unit_data
        const unitExists = budget.unit_data.some((u: any) => u.id === position.unit);
        if (!unitExists) {
          errors.push(`External position ${position.position_name} references invalid unit ${position.unit}`);
        }
      }
    });
  }

  // Calculation figures validation for related units
  // Skip this validation entirely - 0 or undefined is acceptable
  // The dynamic form will handle initialization
  // if (budget.unit_data && budget.calculation_figures) {
  //   const relatedUnits = budget.unit_data.filter((u: any) => u.relationship === 'related');
  //   relatedUnits.forEach((unit: any) => {
  //     // Convert unit name to camelCase for field key
  //     const fieldKey = unit.name
  //       .toLowerCase()
  //       .replace(/[^a-z0-9]+(.)/g, (_: string, char: string) => char.toUpperCase())
  //       .replace(/^(.)/, (_: string, char: string) => char.toLowerCase());

  //     // Allow 0 as a valid value - only check for undefined or null
  //     if (budget.calculation_figures[fieldKey] === undefined ||
  //         budget.calculation_figures[fieldKey] === null) {
  //       errors.push(`Missing calculation figure for ${unit.name}`);
  //     }
  //   });
  // }

  // Phases validation
  if (budget.phases && budget.phases.length > 0) {
    budget.phases.forEach((phase: any, index: number) => {
      if (!phase.name) {
        errors.push(`Phase ${index + 1} has no name`);
      }

      // Date validation can be disabled via env var
      if (isPhaseValidationEnabled()) {
        if (!phase.start_date) {
          errors.push(`Phase ${phase.name || index + 1} has no start date`);
        }
        if (!phase.end_date) {
          errors.push(`Phase ${phase.name || index + 1} has no end date`);
        }
        if (phase.start_date && phase.end_date) {
          const phaseStart = new Date(phase.start_date);
          const phaseEnd = new Date(phase.end_date);
          if (phaseStart >= phaseEnd) {
            errors.push(`Phase ${phase.name || index + 1}: end date must be after start date`);
          }
        }
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Budget acceptance validation result
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates budget ID is a real database ID (not optimistic)
 *
 * Pure function with NO defensive programming.
 * Direct access to field - will crash if budgetId is undefined (intentional).
 *
 * @param budgetId - Budget ID to validate
 * @returns ValidationResult with error if invalid
 * @throws Error if budgetId is not provided
 *
 * @example
 * const result = validateBudgetId(123);
 * if (!result.isValid) console.error(result.error);
 */
export function validateBudgetId(budgetId: number): ValidationResult {
  if (budgetId <= 0) {
    return {
      isValid: false,
      error: 'Budget ID must be a positive integer'
    };
  }
  return { isValid: true };
}

/**
 * Validates budget ID is not an optimistic temporary ID
 *
 * Optimistic IDs are negative numbers used before server save.
 * Pure function with direct access - no defensive checks.
 *
 * @param budgetId - Budget ID to validate
 * @returns ValidationResult with error if optimistic
 * @throws Error if budgetId is not provided
 *
 * @example
 * const result = validateNotOptimistic(-123);
 * if (!result.isValid) console.error(result.error);
 */
export function validateNotOptimistic(budgetId: number): ValidationResult {
  if (budgetId < 0) {
    return {
      isValid: false,
      error: 'Budget has not been saved yet'
    };
  }
  return { isValid: true };
}

/**
 * Validates budget completion percentage for acceptance
 *
 * Budget must be 100% complete to accept.
 * Pure function - direct field access only.
 *
 * @param completion - Budget completion percentage (0-100)
 * @returns ValidationResult with error if incomplete
 * @throws Error if completion is not provided
 *
 * @example
 * const result = validateCompletion(75);
 * if (!result.isValid) console.error(result.error);
 */
export function validateCompletion(completion: number): ValidationResult {
  if (completion < 100) {
    return {
      isValid: false,
      error: `Budget is ${completion}% complete. Must be 100% to accept.`
    };
  }
  return { isValid: true };
}

/**
 * Combined validation for budget acceptance
 *
 * Validates all requirements for accepting a budget:
 * 1. Valid budget ID (positive integer)
 * 2. Not optimistic ID (not temporary/unsaved)
 * 3. 100% completion
 *
 * Pure function with direct access - NO defensive programming.
 * Will crash if budgetId or completion are undefined (intentional fail-fast).
 *
 * @param budgetId - Budget ID
 * @param completion - Budget completion percentage
 * @returns ValidationResult with first error encountered
 * @throws Error if required parameters missing
 *
 * @example
 * const result = validateBudgetAcceptance(123, 100);
 * if (!result.isValid) {
 *   alert(result.error);
 *   return;
 * }
 * // Proceed with acceptance
 */
export function validateBudgetAcceptance(
  budgetId: number,
  completion: number
): ValidationResult {
  // Validate ID
  const idResult = validateBudgetId(budgetId);
  if (!idResult.isValid) {
    return idResult;
  }

  // Validate not optimistic
  const optimisticResult = validateNotOptimistic(budgetId);
  if (!optimisticResult.isValid) {
    return optimisticResult;
  }

  // Validate completion
  const completionResult = validateCompletion(completion);
  if (!completionResult.isValid) {
    return completionResult;
  }

  return { isValid: true };
}